import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import { Badge } from "components/ui/badge";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { PhaseDuration } from "../types/statistics.types";
import { helpdeskPhaseOrder } from "../../helpdeskPhaseConfig";

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Bordvolgorde van de fases. Afgeleid uit `helpdeskPhaseConfig` en niet
// overgeschreven: deze kopie liep anders achter zodra er een fase bijkwam, en
// dan viel de nieuwe fase (index -1) onderaan de grafiek.
const PHASE_ORDER: string[] = helpdeskPhaseOrder;

interface PhaseDurationChartProps {
  data: PhaseDuration[] | null;
  loading: boolean;
}

export function PhaseDurationChart({ data, loading }: PhaseDurationChartProps) {
  // Find the bottleneck phase
  const bottleneckPhase = useMemo(() => {
    return data?.find((p) => p.isBottleneck);
  }, [data]);

  const chartData = useMemo(() => {
    if (!data) return null;

    // Sort by Kanban workflow order and filter out completed phase
    const sortedData = [...data]
      .filter((p) => p.phase !== "Voltooid")
      .sort((a, b) => {
        const aIndex = PHASE_ORDER.indexOf(a.phase);
        const bIndex = PHASE_ORDER.indexOf(b.phase);
        // Put unknown phases at the end
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });

    return {
      labels: sortedData.map((d) => d.phaseLabel),
      datasets: [
        {
          label: "Gemiddelde dagen",
          data: sortedData.map((d) => d.avgDays),
          backgroundColor: sortedData.map((d) =>
            d.isBottleneck ? "rgba(234, 129, 98, 0.8)" : "rgba(91, 115, 4, 0.7)"
          ),
          borderColor: sortedData.map((d) =>
            d.isBottleneck ? "#EA8162" : "#5B7304"
          ),
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  }, [data]);

  const chartOptions = useMemo(
    () => ({
      indexAxis: "y" as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "#31403B",
          titleColor: "#F9F0EE",
          bodyColor: "#F9F0EE",
          borderColor: "#EA8162",
          borderWidth: 1,
          padding: 12,
          callbacks: {
            label: (context: any) => {
              const dataIndex = context.dataIndex;
              const sortedData = [...(data || [])]
                .filter((p) => p.phase !== "Voltooid")
                .sort((a, b) => {
                  const aIndex = PHASE_ORDER.indexOf(a.phase);
                  const bIndex = PHASE_ORDER.indexOf(b.phase);
                  if (aIndex === -1) return 1;
                  if (bIndex === -1) return -1;
                  return aIndex - bIndex;
                });
              const phaseData = sortedData[dataIndex];
              return [
                `Gemiddeld: ${phaseData?.avgDays?.toFixed(1) ?? 0} dagen`,
                `Mediaan: ${phaseData?.medianDays?.toFixed(1) ?? 0} dagen`,
                `Aantal: ${phaseData?.transitionCount ?? 0} tickets`,
              ];
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: {
            color: "rgba(49, 64, 59, 0.1)",
          },
          title: {
            display: true,
            text: "Dagen",
          },
        },
        y: {
          grid: {
            display: false,
          },
        },
      },
    }),
    [data]
  );

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-lg">Tijd per Fase</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Tijd per Fase</CardTitle>
          {bottleneckPhase && (
            <Badge variant="destructive" className="bg-[#EA8162]">
              Bottleneck: {bottleneckPhase.phaseLabel}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Gemiddelde doorlooptijd per status (sinds 01/11/2025)
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          {chartData && <Bar data={chartData} options={chartOptions} />}
        </div>
      </CardContent>
    </Card>
  );
}
